// Global packages and components
import { clsx } from 'clsx'
import { useState, useEffect } from 'react'
import { Link, useLocation, useRouteLoaderData } from '@remix-run/react'
import { getCurrencySymbol, formatPrice } from '~/utils'
import {
  OffcanvasNav,
  AccountPopover,
  ProductSearch,
  ContentEditor,
  Trustpilot,
  SiteMessage,
  HeaderNav,
} from '~/components'
import { useSanityImages } from '~/hooks'
import {
  ShoppingBagIcon,
  UserIcon,
  Bars3Icon,
} from '@heroicons/react/24/outline'

// Types
import { RootDataProps } from '~/types'

interface Props {
  basketTotal?: number
  uspBlockShown?: boolean
}

// Main export
const Header = ({ basketTotal, uspBlockShown }: Props = {}) => {
  // Global data
  const { site } = useRouteLoaderData('root') as RootDataProps
  const [offcanvasOpen, setOffcanvasOpen] = useState<boolean>(false)
  const location = useLocation()

  const primaryNav = site?.settings?.primaryNavigation
    ? site.navigations.find(e => e._id == site.settings.primaryNavigation._ref)
    : null
  const mobileNav = site?.settings?.mobileNavigation
    ? site.navigations.find(e => e._id == site.settings.mobileNavigation._ref)
    : null

  // Logo image
  const { urlFor } = useSanityImages()
  const logoUrl = site?.global?.logo?.asset
    ? urlFor(site?.global?.logo).height(56).auto('format').url()
    : null

  // Close nav when page path changes
  useEffect(() => {
    setOffcanvasOpen(false)
  }, [location.pathname])

  // On change of offcanvas elements
  useEffect(() => {
    // Remove scrollbar when demo open
    if (offcanvasOpen) {
      document.body.classList.add('overflow-hidden')
    } else {
      document.body.classList.remove('overflow-hidden')
    }
  }, [offcanvasOpen])

  return (
    <>
      {site?.global?.siteMessage && (
        <SiteMessage>
          <ContentEditor content={site.global.siteMessage} />
        </SiteMessage>
      )}
      <div className="bg-neutral-50">
        <div className="container pt-5">
          <div
            className={clsx(
              'flex flex-wrap items-center justify-between gap-5 md:flex-nowrap',
              {
                'mb-5': !uspBlockShown,
                'mb-2': uspBlockShown,
              }
            )}
          >
            <div className="order-1">
              {logoUrl && (
                <Link to="/">
                  <img
                    src={logoUrl}
                    alt={site?.global?.logo?.alt || 'HSS Hire home'}
                    className="h-14"
                    height={56}
                    width="auto"
                  />
                </Link>
              )}
            </div>
            <div className="order-5 flex w-full md:order-2 md:w-auto md:flex-1 md:justify-center">
              <div className="w-full md:max-w-[30rem]">
                <ProductSearch />
              </div>
            </div>
            {site?.settings?.showHeaderTrustpilot !== false && (
              <div className="order-3 hidden lg:block">
                <Trustpilot templateId="mini" />
              </div>
            )}
            <div className="order-4 hidden items-center gap-3 lg:flex">
              <AccountPopover />
              <Link
                to="/basket"
                className="flex items-center rounded-3xl bg-neutral-100 px-4 py-2.5"
              >
                <ShoppingBagIcon className="mr-2 size-5" />
                <span className="sr-only">Basket total </span>
                <span>
                  {getCurrencySymbol('GBP')}
                  {basketTotal ? formatPrice(basketTotal) : '0.00'}
                </span>
              </Link>
            </div>
            <div className="order-2 flex flex-wrap items-center gap-2 md:order-5 lg:hidden">
              <Link
                to="/basket"
                className="flex items-center rounded-3xl bg-neutral-100 px-4 py-2.5"
              >
                <ShoppingBagIcon className="mr-2 size-5" />
                <span className="sr-only">Basket total </span>
                <span className="text-sm">
                  {getCurrencySymbol('GBP')}
                  {basketTotal ? formatPrice(basketTotal) : '0.00'}
                </span>
              </Link>
              <Link
                to="/account"
                className="flex size-10 items-center justify-center rounded-full bg-neutral-100"
              >
                <UserIcon className="size-5" />
                <span className="sr-only">My account</span>
              </Link>
              <button
                type="button"
                aria-label="Open menu"
                className="flex size-10 items-center justify-center rounded-full bg-neutral-100"
                onClick={() => setOffcanvasOpen(true)}
              >
                <Bars3Icon className="size-6" />
              </button>
            </div>
          </div>
          {primaryNav?.items && <HeaderNav items={primaryNav.items} />}
        </div>
      </div>
      {mobileNav?.items && (
        <OffcanvasNav
          open={offcanvasOpen}
          items={mobileNav.items}
          setOpen={setOffcanvasOpen}
        />
      )}
    </>
  )
}

export default Header
